import React, { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Modal,
  Box,
  Typography,
  Container,
  Paper,
  Grid,
  styled,
  CircularProgress,
  SelectChangeEvent,
  useTheme,
  IconButton,
  alpha,
  ListItemText
} from '@mui/material';
import { httpRequest } from 'src/services/httpRequest';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';

// TypeScript interfaces
interface FormData {
  firstName: string;
  lastName: string;
  business: string;
  federalID: string;
  phone: string;
  email: string;
  fax: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  categories: number[]; // Store category IDs
  unsubscribe: boolean;
}

interface ApiResponse {
  success?: boolean;
  message?: string;
  status?: string;
}

interface BusinessResponse {
  status: string;
  message: string;
  data: {
    TownshipID: number;
    FederalID: string;
    Business: string;
    Address1: string;
    Address2: string;
    City: string;
    State: string;
    ZipCode: string;
    ParentID: number;
  };
}

interface VendorResponse {
  status: string;
  message: string;
  data: {
    UserID: number;
    FirstName: string;
    LastName: string;
    Business: string;
    FederalID: string;
    PhoneNumber: string;
    EmailID: string;
    FaxNumber: string;
    Address1: string;
    Address2: string;
    City: string;
    State: string;
    ZipCode: string;
    BidCategories?: Array<{ id: number; name: string }>; // Older format
    BidCategoryIDs?: number[]; // New format
    UnSubscribeNotify?: boolean;
  };
}

interface CategoryResponse {
  status: string;
  message: string;
  data: {
    totalRecords: number;
    data: Array<{
      CategoryID: number;
      CategoryName: string;
    }>;
  };
}

// For category dropdown display/selection helper
interface CategoryItem {
  id: number;
  name: string;
}

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
  minHeight: 'calc(100vh - 200px)' // Ensure enough space for dropdowns
}));

const FormSection = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(4),
  '& .MuiTypography-h6': {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    color: theme.palette.text.primary
  }
}));

const ButtonContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(2),
  marginTop: theme.spacing(3)
}));

const LoadingContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginTop: '20px'
});

const states = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY'
];

const VendorRegistration: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const params = useParams<{ UserID?: string }>();
  const userId = params.UserID;

  // State to track if we're in edit mode (UserID present)
  const [isEditMode] = useState<boolean>(!!userId);

  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    business: '',
    federalID: '',
    phone: '',
    email: '',
    fax: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    categories: [], // Store category IDs
    unsubscribe: false
  });

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [, setApiError] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [fieldsFetched, setFieldsFetched] = useState<boolean>(false);
  const [federalIdError, setFederalIdError] = useState<string | null>(null);
  const [federalIdEntered, setFederalIdEntered] = useState<boolean>(false); // New state to track if Federal ID is entered
  const [fetchingBusinessData, setFetchingBusinessData] =
    useState<boolean>(false);
  const [fetchingVendorData, setFetchingVendorData] = useState<boolean>(false);
  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const [loadingCategories, setLoadingCategories] = useState<boolean>(false);
  const [isManualInputAllowed, setIsManualInputAllowed] =
    useState<boolean>(false);
  const [zipError, setZipError] = useState<string | null>(null);
  const [openCategorySelect, setOpenCategorySelect] = useState(false);

  // Phone number formatting function
  const formatPhoneNumber = (value: string) => {
    const input = value.replace(/\D/g, '').substring(0, 10);
    const first = input.substring(0, 3);
    const middle = input.substring(3, 6);
    const last = input.substring(6, 10);
    let finalValue = '';
    if (input?.length > 6) {
      finalValue = `${first}-${middle}-${last}`;
    } else if (input?.length > 3) {
      finalValue = `${first}-${middle}`;
    } else if (input?.length >= 0) {
      finalValue = input;
    }
    return finalValue;
  };

  // Add this validation function
  const validateZipCode = (zip: string): boolean => {
    // Match either 5 digits or 5 digits-4 digits format
    const regex = /^\d{5}(-\d{4})?$/;
    return regex.test(zip);
  };

  const formatFaxNumber = (value: string) => {
    const input = value.replace(/\D/g, '').substring(0, 10); // Keep only the first 10 digits
    const first = input.substring(0, 3);
    const middle = input.substring(3, 6);
    const last = input.substring(6, 10);
    let finalValue = '';
    if (input?.length > 6) {
      finalValue = `${first}-${middle}-${last}`;
    } else if (input?.length > 3) {
      finalValue = `${first}-${middle}`;
    } else if (input?.length >= 0) {
      finalValue = input;
    }
    return finalValue;
  };

  // Fetch categories from API
  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = (await httpRequest(
        'GET',
        `/bid_portal/category/get_all`
      )) as CategoryResponse;

      if (
        response.status === 'success' &&
        response.data &&
        response.data.data
      ) {
        // Filter out inactive categories
        const activeCategories = response.data.data.map((cat) => ({
          id: cat.CategoryID,
          name: cat.CategoryName
        }));

        setCategories(activeCategories);
      } else {
        throw new Error(response.message || 'Failed to retrieve categories');
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      if (error instanceof Error) {
        setApiError(`Error fetching categories: ${error.message}`);
      } else {
        setApiError('An unknown error occurred while fetching categories');
      }
    } finally {
      setLoadingCategories(false);
    }
  };

  // Fetch vendor data based on UserID
  const fetchVendorData = async (userID: string) => {
    setFetchingVendorData(true);
    setApiError(null);

    try {
      const response = (await httpRequest(
        'GET',
        `/auth/vendor/${userID}`
      )) as VendorResponse;

      if (response.status === 'success' && response.data) {
        // Extract category IDs from the response
        // Handle both data formats (BidCategories or BidCategoryIDs)
        let categoryIds: number[] = [];

        if (response.data[0].BidCategoryIDs) {
          // New format with direct category IDs array
          categoryIds = response.data[0].BidCategoryIDs;
        } else if (response.data[0].BidCategories) {
          // Old format with objects containing id and name
          categoryIds = response.data[0].BidCategories.map((cat) => cat.id);
        }

        // Populate form with the data[0] from the API response
        setFormData({
          firstName: response.data[0].FirstName || '',
          lastName: response.data[0].LastName || '',
          business: response.data[0].Business || '',
          federalID: response.data[0].FederalID || '',
          phone: response.data[0].PhoneNumber || '',
          email: response.data[0].EmailID || '',
          fax: response.data[0].FaxNumber || '',
          address1: response.data[0].Address1 || '',
          address2: response.data[0].Address2 || '',
          city: response.data[0].City || '',
          state: response.data[0].State || '',
          zip: response.data[0].ZipCode || '',
          categories: categoryIds,
          unsubscribe: response.data[0].UnSubscribeNotify || false
        });

        setFieldsFetched(true);
        setFederalIdEntered(true);
      } else {
        throw new Error(
          response.message || 'Failed to retrieve vendor details'
        );
      }
    } catch (error) {
      console.error('Error fetching vendor data:', error);
      if (error instanceof Error) {
        setApiError(`Error fetching vendor data: ${error.message}`);
      } else {
        setApiError('An unknown error occurred while fetching vendor data');
      }
      setFieldsFetched(false);
    } finally {
      setFetchingVendorData(false);
    }
  };

  useEffect(() => {
    // Fetch categories from API
    fetchCategories();

    // If UserID is provided, fetch vendor data
    if (userId) {
      fetchVendorData(userId);
    }
  }, [userId]);

  // Format Federal ID to automatically include hyphen
  const formatFederalId = (value: string): string => {
    // If the value already has a properly positioned hyphen, keep it
    if (value.length >= 3 && value[2] === '-') {
      // Keep the hyphen but ensure other characters are numeric
      const part1 = value.substring(0, 2).replace(/\D/g, '');
      const part2 = value.substring(3).replace(/\D/g, '');

      return `${part1}-${part2}`;
    }

    // Remove all non-numeric characters
    const numbersOnly = value.replace(/\D/g, '');

    // Format with hyphen
    if (numbersOnly.length <= 2) {
      return numbersOnly;
    } else {
      return `${numbersOnly.substring(0, 2)}-${numbersOnly.substring(
        2,
        Math.min(9, numbersOnly.length)
      )}`;
    }
  };

  // Validate Federal ID format (00-0000000)
  const validateFederalId = (id: string): boolean => {
    const regex = /^\d{2}-\d{7}$/;
    return regex.test(id);
  };

  // Fetch business data based on Federal ID
  const fetchBusinessData = async (federalId: string) => {
    if (isEditMode) return;

    setFetchingBusinessData(true);
    setApiError(null);

    try {
      const response = (await httpRequest(
        'GET',
        `/auth/business/${federalId}`
      )) as BusinessResponse;

      if (response.status === 'success' && response.data) {
        // Populate form with the data from the API response
        setFormData((prev) => ({
          ...prev,
          business: response.data.Business || '',
          federalID: response.data.FederalID || federalId,
          address1: response.data.Address1 || '',
          address2: response.data.Address2 || '',
          city: response.data.City || '',
          state: response.data.State || '',
          zip: response.data.ZipCode || ''
        }));

        setFieldsFetched(true);
      } else {
        // Enable fields for manual input if business is not found
        setApiError(
          'Business information not found. You may enter the details manually.'
        );
        setFieldsFetched(false);
        setIsManualInputAllowed(true); // Enable manual input
      }
    } catch (error) {
      console.error('Error fetching business data:', error);
      setApiError(
        'An unknown error occurred while fetching business data. You may enter the details manually.'
      );
      setFieldsFetched(false);
      setIsManualInputAllowed(true); // Enable manual input on error
    } finally {
      setFetchingBusinessData(false);
      setFederalIdEntered(true);
    }
  };

  const formatZipCode = (value: string): string => {
    // Remove all non-numeric characters
    const numbersOnly = value.replace(/\D/g, '').substring(0, 9); // Limit to 9 digits

    // Format with hyphen for zip codes longer than 5 digits
    if (numbersOnly.length <= 5) {
      return numbersOnly;
    } else {
      return `${numbersOnly.substring(0, 5)}-${numbersOnly.substring(5, 9)}`;
    }
  };

  // Handle text and checkbox input changes
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;

    if (!name) return;

    if (type === 'checkbox') {
      setFormData((prev) => ({
        ...prev,
        [name]: checked
      }));
    } else if (name === 'federalID') {
      // Skip processing if in edit mode
      if (isEditMode) return;

      // Allow for manual hyphen entry or add automatically
      const formattedValue = formatFederalId(value);

      // Only update if the formatted value is different to avoid cursor jumping
      if (formattedValue !== value) {
        setFormData((prev) => ({
          ...prev,
          [name]: formattedValue
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          [name]: value
        }));
      }

      // Handle Federal ID validation and fetch business data
      if (formattedValue && validateFederalId(formattedValue)) {
        setFederalIdError(null);
        fetchBusinessData(formattedValue);
        setFederalIdEntered(true);
      } else if (formattedValue) {
        setFederalIdError('Federal Tax ID must be in format XX-XXXXXXX');
        setFieldsFetched(false);

        // Reset fields when the Federal ID is invalid or removed
        setFormData((prev) => ({
          ...prev,
          business: '',
          address1: '',
          address2: '',
          city: '',
          state: '',
          zip: '',
          categories: [] // Clear the categories
        }));
        setZipError(null);

        setFederalIdEntered(formattedValue.length === 10); // Only set true when close to correct format (XX-XXXXXXX)
      } else {
        setFederalIdError(null);
        setFieldsFetched(false);
        setFederalIdEntered(false);

        // Reset fields when the Federal ID is removed
        setFormData((prev) => ({
          ...prev,
          business: '',
          address1: '',
          address2: '',
          city: '',
          state: '',
          zip: '',
          categories: [] // Clear the categories
        }));
        setZipError(null);
      }
    } else if (name === 'phone' || name === 'fax') {
      // Format phone and fax numbers automatically
      const formattedValue =
        name === 'phone' ? formatPhoneNumber(value) : formatFaxNumber(value);
      setFormData((prev) => ({
        ...prev,
        [name]: formattedValue
      }));
    } else if (name === 'zip') {
      const formattedValue = formatZipCode(value);
      setFormData((prev) => ({
        ...prev,
        [name]: formattedValue
      }));

      // Validate zip code format
      if (formattedValue && !validateZipCode(formattedValue)) {
        setZipError(
          'ZIP code must be 5 digits (e.g., 18235) or 9 digits (e.g., 18235-2239)'
        );
      } else {
        setZipError(null);
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSelectChange = (event: SelectChangeEvent<unknown>) => {
    const { name, value } = event.target;

    if (!name) return;

    if (name === 'categories') {
      if (value === 'select-all') {
        setFormData((prev) => ({
          ...prev,
          categories:
            prev.categories.length === categories.length
              ? []
              : categories.map((category) => category.id)
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          categories: value as number[]
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleClear = () => {
    // If in edit mode, only clear the editable fields
    if (isEditMode && userId) {
      setFormData((prev) => ({
        ...prev,
        // Only clear the editable fields in edit mode
        firstName: '',
        lastName: '',
        phone: '',
        fax: '',
        categories: [],
        unsubscribe: false
        // Keep the non-editable fields: business, federalID, email, address1, address2, city, state, zip
      }));
      setZipError(null); // Add this line

      // Clear any errors
      setApiError(null);
      setFederalIdError(null);
      return;
    }

    // For non-edit mode (registration), clear all fields as before
    setFormData({
      firstName: '',
      lastName: '',
      business: '',
      federalID: '',
      phone: '',
      email: '',
      fax: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
      categories: [],
      unsubscribe: false
    });
    setApiError(null);
    setFieldsFetched(false);
    setFederalIdError(null);
    setFederalIdEntered(false);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Validate Federal ID format before submitting
    if (!isEditMode && !validateFederalId(formData.federalID)) {
      setFederalIdError('Federal Tax ID must be in format XX-XXXXXXX');
      return;
    }

    if (!validateZipCode(formData.zip)) {
      setZipError(
        'ZIP code must be 5 digits (e.g., 18235) or 9 digits (e.g., 18235-2239)'
      );
      return;
    }

    setIsLoading(true);
    setApiError(null);

    try {
      const payload = {
        FirstName: formData.firstName,
        LastName: formData.lastName,
        EmailID: formData.email,
        PhoneNumber: formData.phone, // This will be in XXX-XXX-XXXX format
        Address1: formData.address1,
        Address2: formData.address2,
        City: formData.city,
        State: formData.state,
        ZipCode: formData.zip,
        Business: formData.business,
        BidCategoryIDs: formData.categories, // Submit category IDs directly
        FederalID: formData.federalID,
        FaxNumber: formData.fax || '',
        UnSubscribeNotify: formData.unsubscribe,
        ...(isEditMode ? {} : { IsAdminRegisterVendor: true })
      };

      // Make API call - different endpoint based on edit mode
      const endpoint = isEditMode
        ? `/auth/updateUser/${userId}`
        : `/auth/register`;

      const method = isEditMode ? 'PATCH' : 'POST';

      const response = (await httpRequest(
        method,
        endpoint,
        payload
      )) as ApiResponse;

      if (response.status === 'error') {
        throw new Error(response.message || 'Operation failed');
      }

      setModalContent(
        isEditMode
          ? 'Vendor information has been successfully updated in the system.'
          : 'Vendor registration has been successfully submitted to the system.'
      );
      setOpenModal(true);

      if (!isEditMode) {
        handleClear();
      }
    } catch (error) {
      console.error('Operation error:', error);
      if (error instanceof Error) {
        setApiError(error.message);
      } else {
        setApiError('An unknown error occurred');
      }
      setModalContent(error.message);
      setOpenModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalClose = () => {
    setOpenModal(false);
    if (modalContent.includes('success')) {
      navigate(-1);
    }
  };

  // Determine if a field should be disabled based on edit mode and field type
  const isFieldDisabled = (fieldName: string): boolean => {
    // If manual input is allowed, all fields should be enabled
    if (isManualInputAllowed) {
      return false;
    }

    const disabledFieldsInEditMode = [
      'business',
      'federalID',
      'address1',
      'address2',
      'city',
      'state',
      'zip',
      'email'
    ];

    const disabledFieldsInAddMode = [
      'business',
      'address1',
      'address2',
      'city',
      'state',
      'zip'
    ];

    if (isEditMode && disabledFieldsInEditMode.includes(fieldName)) {
      return true;
    }

    if (!isEditMode && disabledFieldsInAddMode.includes(fieldName)) {
      return true;
    }

    // For regular mode, only disable fields until Federal ID is entered and validated
    if (!isEditMode && !federalIdEntered) {
      return fieldName !== 'federalID';
    }

    return false;
  };

  const getCategoryNamesByIds = (categoryIds: number[]): string => {
    if (!categories.length || !categoryIds.length) return '';

    const selectedCategories = categories.filter((cat) =>
      categoryIds.includes(cat.id)
    );
    return selectedCategories.map((cat) => cat.name).join(', ');
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mt: 3,
          ml: 2,
          gap: 2
        }}
      >
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.2)
            }
          }}
        >
          <ArrowBack />
        </IconButton>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            color: theme.palette.primary.main,
            position: 'relative',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: isEditMode ? 120 : 150,
              height: 4,
              borderRadius: 2,
              backgroundColor: theme.palette.primary.main
            }
          }}
        >
          {isEditMode ? 'Update Vendor ' : 'Vendor Registration'}
        </Typography>
      </Box>
      <Container maxWidth="md">
        <StyledPaper elevation={3}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            {isEditMode ? 'Update Vendor Information' : 'Vendor Registration'}
          </Typography>

          {/* {apiError && (
            <Alert
              severity={
                apiError.includes('You may enter the details manually')
                  ? 'info'
                  : 'error'
              }
              sx={{ mb: 3 }}
            >
              {apiError}
            </Alert>
          )} */}

          {(fetchingVendorData ||
            fetchingBusinessData ||
            loadingCategories) && (
            <LoadingContainer>
              <CircularProgress size={24} />
              <Typography variant="body2" sx={{ ml: 2 }}>
                {loadingCategories
                  ? 'Loading categories...'
                  : fetchingVendorData
                  ? 'Loading vendor data...'
                  : 'Fetching business information...'}
              </Typography>
            </LoadingContainer>
          )}

          <form onSubmit={handleSubmit}>
            {/* Federal Tax ID Section */}
            <FormSection>
              <Typography variant="h6" gutterBottom>
                Business Identification
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Federal Tax ID"
                    name="federalID"
                    value={formData.federalID}
                    onChange={handleInputChange}
                    required
                    variant="outlined"
                    placeholder="XX-XXXXXXX"
                    helperText={
                      federalIdError ||
                      'Enter Federal Tax ID in format: XX-XXXXXXX'
                    }
                    error={!!federalIdError}
                    disabled={
                      isFieldDisabled('federalID') || fetchingBusinessData
                    }
                    InputProps={{
                      inputProps: {
                        maxLength: 10 // 2 digits + hyphen + 7 digits
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </FormSection>

            {/* Business & Contact Information */}
            <FormSection>
              <Typography variant="h6" gutterBottom>
                Business & Contact Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Business Name"
                    name="business"
                    value={formData.business}
                    onChange={handleInputChange}
                    required
                    variant="outlined"
                    disabled={isFieldDisabled('business')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    variant="outlined"
                    disabled={isFieldDisabled('firstName')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    variant="outlined"
                    disabled={isFieldDisabled('lastName')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    required
                    fullWidth
                    label="Phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    variant="outlined"
                    disabled={isFieldDisabled('phone')}
                    placeholder="XXX-XXX-XXXX"
                    inputProps={{ maxLength: 12 }} // Updated to allow for formatted phone
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    variant="outlined"
                    disabled={isFieldDisabled('email')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Fax"
                    name="fax"
                    value={formData.fax}
                    onChange={handleInputChange}
                    variant="outlined"
                    placeholder="XXX-XXX-XXXX"
                    disabled={isFieldDisabled('fax')}
                  />
                </Grid>
              </Grid>
            </FormSection>

            {/* Address Information */}
            <FormSection>
              <Typography variant="h6" gutterBottom>
                Address Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address Line 1"
                    name="address1"
                    value={formData.address1}
                    onChange={handleInputChange}
                    required
                    variant="outlined"
                    disabled={isFieldDisabled('address1')}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address Line 2"
                    name="address2"
                    value={formData.address2}
                    onChange={handleInputChange}
                    variant="outlined"
                    disabled={isFieldDisabled('address2')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="City"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    required
                    variant="outlined"
                    disabled={isFieldDisabled('city')}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>State</InputLabel>
                    <Select
                      name="state"
                      value={formData.state}
                      onChange={handleSelectChange}
                      label="State"
                      required
                      disabled={isFieldDisabled('state')}
                    >
                      <MenuItem value="">
                        <em>Select State</em>
                      </MenuItem>
                      {states.map((state) => (
                        <MenuItem key={state} value={state}>
                          {state}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="ZIP Code"
                    name="zip"
                    value={formData.zip}
                    onChange={handleInputChange}
                    required
                    variant="outlined"
                    disabled={isFieldDisabled('zip')}
                    placeholder="12345 or 12345-6789"
                    helperText={zipError}
                    error={!!zipError}
                    inputProps={{ maxLength: 10 }} // 5 digits + hyphen + 4 digits
                  />
                </Grid>
              </Grid>
            </FormSection>

            <FormSection>
              {/* Add bottom padding */}
              <Typography variant="h6" gutterBottom>
                Business Categories *
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel id="categories-label">Categories</InputLabel>
                    <Select
                      open={openCategorySelect}
                      onOpen={() => setOpenCategorySelect(true)}
                      onClose={() => setOpenCategorySelect(false)}
                      required
                      labelId="categories-label"
                      multiple
                      name="categories"
                      value={formData.categories}
                      onChange={(e) => {
                        const selectedValues = e.target.value as any[];

                        if (selectedValues.includes('SELECT_ALL')) {
                          const allCategoryIDs = categories.map(
                            (cat) => cat.id
                          );
                          const isAllSelected =
                            formData.categories.length === categories.length;

                          const finalCategories = isAllSelected
                            ? []
                            : allCategoryIDs;

                          setFormData((prev) => ({
                            ...prev,
                            categories: finalCategories
                          }));
                        } else {
                          setFormData((prev) => ({
                            ...prev,
                            categories: selectedValues
                          }));
                        }
                      }}
                      label="Categories"
                      renderValue={(selected) =>
                        getCategoryNamesByIds(selected as number[])
                      }
                      disabled={
                        isFieldDisabled('categories') || loadingCategories
                      }
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 350,
                            borderRadius: 1.5,
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                            marginTop: 1
                          }
                        },
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left'
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left'
                        }
                      }}
                    >
                      <MenuItem value="SELECT_ALL">
                        <Checkbox
                          checked={
                            formData.categories.length === categories.length
                          }
                          indeterminate={
                            formData.categories.length > 0 &&
                            formData.categories.length < categories.length
                          }
                        />
                        <ListItemText
                          primary="Select All"
                          sx={{ fontWeight: 'bold', color: 'primary.main' }}
                        />
                      </MenuItem>

                      {categories.map((category) => (
                        <MenuItem key={category.id} value={category.id}>
                          <Checkbox
                            checked={formData.categories.includes(category.id)}
                          />
                          <Typography>{category.name}</Typography>
                        </MenuItem>
                      ))}

                      <MenuItem
                        disableRipple
                        sx={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          borderTop: '1px solid #ddd',
                          mt: 1,
                          pt: 1
                        }}
                      >
                        <button
                          style={{
                            backgroundColor: '#1976d2',
                            color: 'white',
                            padding: '5px 12px',
                            borderRadius: '6px',
                            border: 'none',
                            cursor: 'pointer',
                            fontWeight: '600'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenCategorySelect(false);
                          }}
                        >
                          Apply
                        </button>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </FormSection>
            {/* Preferences */}
            <FormSection>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.unsubscribe}
                    onChange={handleInputChange}
                    name="unsubscribe"
                    disabled={isFieldDisabled('unsubscribe')}
                  />
                }
                label="Unsubscribe from notifications"
              />
            </FormSection>

            {/* Buttons */}
            <ButtonContainer>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleClear}
                disabled={isLoading}
              >
                Clear
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={
                  isLoading ||
                  (!isEditMode && !validateFederalId(formData.federalID))
                }
              >
                {isLoading
                  ? 'Submitting...'
                  : isEditMode
                  ? 'Update'
                  : 'Submit Registration'}
              </Button>
            </ButtonContainer>

            {isLoading && (
              <LoadingContainer>
                <CircularProgress size={24} />
              </LoadingContainer>
            )}
          </form>
        </StyledPaper>
        {/* Result Modal */}
        <Modal
          open={openModal}
          onClose={handleModalClose}
          aria-labelledby="modal-title"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 400,
              bgcolor: 'background.paper',
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
              textAlign: 'center'
            }}
          >
            {modalContent.includes('success') ? (
              <>
                <Box sx={{ color: 'success.main', mb: 2, fontSize: '3rem' }}>
                  ✓
                </Box>
                <Typography
                  id="modal-title"
                  variant="h5"
                  component="h2"
                  gutterBottom
                  color="primary"
                >
                  {isEditMode
                    ? 'Update Successful!'
                    : 'Registration Successful!'}
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  {modalContent}
                </Typography>
              </>
            ) : (
              <>
                <Box sx={{ color: 'error.main', mb: 2, fontSize: '3rem' }}>
                  ✗
                </Box>
                <Typography
                  id="modal-title"
                  variant="h5"
                  component="h2"
                  gutterBottom
                  color="error"
                >
                  {isEditMode ? 'Update Failed' : 'Registration Failed'}
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  {modalContent}
                </Typography>
              </>
            )}
            <Button
              onClick={handleModalClose}
              variant="contained"
              color={modalContent.includes('success') ? 'primary' : 'error'}
              sx={{ minWidth: 120 }}
            >
              Close
            </Button>
          </Box>
        </Modal>
      </Container>
    </>
  );
};

export default VendorRegistration;
